import { useEffect, useState } from "react";
import { CartProvider, useCart } from "react-use-cart";
import useAuth from "../hooks/useAuth";
import usePrivateApi from "../hooks/usePrivateApi";
import useDebounce from "./../hooks/useDebounce";

const RestoreCartProvider = ({ children }) => {
  const { auth } = useAuth();
  const { emptyCart, setItems, items } = useCart();
  const privateApi = usePrivateApi();

  useEffect(() => {
    const restoreCart = async (customerId) => {
      const res = await privateApi.get(`/carts/${customerId}`);

      if (res.data.cart?.CartLines?.length) {
        const lines = res.data.cart.CartLines;

        // Controlla se il carrello locale ha già gli stessi prodotti
        const localCartCodes = items
          .map((item) => item.Codice)
          .sort()
          .join(",");
        const serverCartCodes = lines
          .map((line) => line.CodiceArticolo)
          .sort()
          .join(",");

        // Se i prodotti sono gli stessi, mantieni i prezzi del localStorage
        if (localCartCodes === serverCartCodes && items.length > 0) {
          // Aggiorna solo le quantità se necessario
          const updatedItems = items.map((item) => {
            const serverLine = lines.find(
              (line) => line.CodiceArticolo === item.Codice
            );
            if (serverLine && serverLine.quantity !== item.quantity) {
              return { ...item, quantity: serverLine.quantity };
            }
            return item;
          });
          setItems(updatedItems);
          return;
        }

        // Altrimenti svuota e ricarica tutto
        emptyCart();

        const promises = lines.map((line) => {
          const encodedCodiceArticolo = encodeURIComponent(line.CodiceArticolo);

          return privateApi.get(
            `/products/${encodedCodiceArticolo}?CodiceSoggetto=${auth.customer.Codice}`
          );
        });

        Promise.allSettled(promises).then((results) => {
          const products = results.reduce((products, { value, status }, i) => {
            if (status === "fulfilled") {
              products.push({
                ...value.data.product,
                id: value.data.product.Codice,
                price: value.data.product.price,
                quantity: lines[i].quantity,
                ref: lines[i].ref,
              });
            }

            return products;
          }, []);

          setItems(products);
        });
      }
    };

    if (!auth?.customer?.id) return;

    restoreCart(auth?.customer.id);
  }, [auth?.accessToken]);

  return children;
};

export const MyCartProvider = ({ children }) => {
  const { auth } = useAuth();
  const privateApi = usePrivateApi();

  const [itemsToUpdate, setItemsToUpdate] = useState([]);

  const debouncedUpdate = useDebounce(itemsToUpdate, 500);

  const mySetItems = (items) => {
    try {
      return privateApi.post(`/carts/${auth.customer.id}`, { items });
    } catch (err) {
      // TODO: Build errors handler
    }
  };

  const myItemAdd = (item) => {
    try {
      return privateApi.post(`/carts/${auth.customer.id}/${item.Codice}`, {
        quantity: item.quantity,
        ref: item.ref,
      });
    } catch (err) {
      // TODO: Build errors handler
    }
  };

  const myItemRemove = (item) => {
    try {
      return privateApi.delete(`/carts/${auth.customer.id}/${item}`);
    } catch (err) {
      // TODO: Build errors handler
    }
  };

  const myItemUpdate = (item) => {
    const index = itemsToUpdate?.findIndex((i) => i.Codice === item.Codice);

    if (index !== -1) {
      setItemsToUpdate((itemsToUpdate) => {
        itemsToUpdate[index] = item;
        return [...itemsToUpdate];
      });
    } else {
      setItemsToUpdate((itemsToUpdate) => {
        return [...itemsToUpdate, item];
      });
    }
  };

  useEffect(() => {
    if (!debouncedUpdate) return;

    const save = async () => {
      for (const index in itemsToUpdate) {
        const item = itemsToUpdate[index];

        try {
          await privateApi.put(`/carts/${auth.customer.id}/${item.Codice}`, {
            quantity: item.quantity,
            ref: item.ref,
          });

          setItemsToUpdate((prev) => []);
        } catch (err) {
          // TODO: Build errors handler
        }
      }
    };

    save();
  }, [debouncedUpdate]);

  return (
    <CartProvider
      onSetItems={(items) => mySetItems(items)}
      onItemAdd={(item) => myItemAdd(item)}
      onItemRemove={(item) => myItemRemove(item)}
      onItemUpdate={(item) => myItemUpdate(item)}
    >
      <RestoreCartProvider>{children}</RestoreCartProvider>
    </CartProvider>
  );
};
